
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, TrendingUp, Users, DollarSign, FileText, Award, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import SEOHead from "@/components/SEOHead";

const Index = () => {
  const features = [
    {
      icon: <Lock className="h-8 w-8 text-slate-900" />,
      title: "Secure Access Only",
      description: "All investment opportunities are exclusively available through our password-protected investor portal."
    },
    {
      icon: <Award className="h-8 w-8 text-slate-900" />,
      title: "Curated Opportunities",
      description: "Each investment opportunity is carefully selected and thoroughly vetted by our experienced team."
    },
    {
      icon: <FileText className="h-8 w-8 text-slate-900" />,
      title: "Comprehensive Analysis",
      description: "Detailed investment profiles with complete due diligence documentation and risk assessment."
    }
  ];

  const investorBenefits = [
    {
      icon: <Shield className="h-8 w-8 text-yellow-800" />,
      title: "Exclusive Portal Access",
      description: "Investment opportunities are only viewable through our secure, password-protected investor portal."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-yellow-800" />,
      title: "High-Yield Returns",
      description: "Access to premium land contracts and private mortgages with attractive yield potential."
    },
    {
      icon: <Users className="h-8 w-8 text-yellow-800" />,
      title: "Individual Access Codes",
      description: "Each qualified investor receives a unique access code for secure portal entry."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEOHead 
        title="Real Estate Investment Opportunities - Land Contracts & Private Mortgages"
        description="Discover exclusive real estate investment opportunities through Contract Funding Group. Access high-yield land contracts and private mortgages with comprehensive due diligence. 25+ years experience."
        keywords="real estate investment, land contracts, private mortgages, investment opportunities, high yield returns, real estate financing, contract funding, investment portal"
        canonicalUrl="https://contract-cash-nexus.lovable.app/"
      />
      
      <Navigation />
      <Hero />
      
      {/* How It Works Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Discover Real Estate's Best Kept Secret
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All investment opportunities are exclusively accessible through our password-protected 
              investor portal. No investments are viewable without proper authentication.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              How to Access Investment Opportunities
            </h3>
            <div className="max-w-2xl mx-auto space-y-4">
              <p className="text-gray-600">
                <strong>Step 1:</strong> Contact us to qualify as an accredited investor
              </p>
              <p className="text-gray-600">
                <strong>Step 2:</strong> Receive your individual access password
              </p>
              <p className="text-gray-600">
                <strong>Step 3:</strong> Use the investor portal to view and bid on opportunities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Access Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-yellow-50 to-yellow-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Unlock Real Estate's Best Kept Secret
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Investment opportunities are only available to qualified investors with 
              individual access codes. Contact us for investor qualification and password assignment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {investorBenefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300 border-yellow-200">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <CardTitle className="text-xl">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-gray-700 mb-6 text-lg">
              Ready to discover real estate's best kept secret?
            </p>
            <Link to="/investor-portal">
              <Button size="lg" className="bg-yellow-800 hover:bg-yellow-900 text-white px-8 py-4 text-lg">
                Enter Investor Portal
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Trusted Investment Partnership</h2>
          <div className="flex justify-center">
            <div>
              <div className="text-4xl font-bold text-yellow-700 mb-2">25+</div>
              <div className="text-gray-300">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Contract Funding Group</h3>
          <p className="text-gray-400 mb-4">
            Real Estate's Best Kept Secret - Exclusive investment opportunities for qualified investors
          </p>
          <p className="text-sm text-gray-500">
            © 2024 Contract Funding Group. All rights reserved. Licensed and insured.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
